import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash, UploadSimple, FloppyDisk, ArrowsClockwise, SignOut } from "@phosphor-icons/react";
import { invalidateSiteContent } from "@/hooks/useSiteContent";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function useAdminAuth() {
  const [token, setToken] = useState(() => localStorage.getItem("pta_portal_token"));
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${BACKEND}/api/auth/me`, { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} });
        setUser(r.data);
      } catch (_) { setUser(null); }
      setChecking(false);
    })();
  }, [token]);
  const headers = () => (token ? { Authorization: `Bearer ${token}` } : {});
  return { user, token, setToken, headers, checking };
}

function StatCard({ label, value }) {
  return (
    <div className="border hairline-navy p-6 bg-[var(--pta-cream)]">
      <div className="overline">{label}</div>
      <div className="mt-2 font-serif-display text-4xl md:text-5xl text-[var(--pta-navy)]">{value}</div>
    </div>
  );
}

function Stats({ headers }) {
  const [s, setS] = useState(null);
  useEffect(() => { axios.get(`${BACKEND}/api/admin/stats`, { headers: headers(), withCredentials: true }).then((r) => setS(r.data)); }, [headers]);
  if (!s) return <div className="text-[var(--pta-ink)]/60">Loading…</div>;
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
      <StatCard label="Enquiries" value={s.enquiries} />
      <StatCard label="Consultations" value={s.consultations} />
      <StatCard label="Newsletter" value={s.newsletter} />
      <StatCard label="Users" value={s.portal_users} />
      <StatCard label="Chat sessions" value={s.chat_sessions} />
    </div>
  );
}

function EnquiriesTab({ headers }) {
  const [rows, setRows] = useState([]);
  const load = async () => setRows((await axios.get(`${BACKEND}/api/admin/enquiries`, { headers: headers(), withCredentials: true })).data);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const setStatus = async (id, status) => {
    await axios.patch(`${BACKEND}/api/admin/enquiries/${id}`, { status }, { headers: headers(), withCredentials: true });
    toast.success("Status updated");
    load();
  };
  return (
    <div className="border hairline-navy">
      <div className="grid grid-cols-12 border-b hairline bg-[var(--pta-paper)] font-mono-fine text-[10px] tracking-[0.22em] uppercase text-[var(--pta-ink)]/60">
        <div className="col-span-2 p-3">Date</div>
        <div className="col-span-2 p-3">Name</div>
        <div className="col-span-3 p-3">Contact</div>
        <div className="col-span-3 p-3">Message</div>
        <div className="col-span-2 p-3">Status</div>
      </div>
      {rows.length === 0 && <div className="p-8 text-center text-[var(--pta-ink)]/60">No enquiries yet.</div>}
      {rows.map((r) => (
        <div key={r.id} data-testid={`enq-row-${r.id}`} className="grid grid-cols-12 border-b hairline last:border-b-0 hover:bg-[var(--pta-paper)]/40">
          <div className="col-span-2 p-3 font-mono-fine text-xs text-[var(--pta-ink)]/70">{r.created_at?.slice?.(0,10) || "—"}</div>
          <div className="col-span-2 p-3">
            <div className="font-serif-display text-base text-[var(--pta-navy)]">{r.name}</div>
            <div className="text-[10px] text-[var(--pta-ink)]/50 font-mono-fine">{r.service || "—"}</div>
          </div>
          <div className="col-span-3 p-3 text-xs text-[var(--pta-ink)]/70">
            <div>{r.email}</div>
            <div>{r.phone}</div>
          </div>
          <div className="col-span-3 p-3 text-xs text-[var(--pta-ink)]/70 line-clamp-3">{r.message}</div>
          <div className="col-span-2 p-3">
            <Select value={r.status || "new"} onValueChange={(v) => setStatus(r.id, v)}>
              <SelectTrigger data-testid={`enq-status-${r.id}`} className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="contacted">Contacted</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
                <SelectItem value="won">Won</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      ))}
    </div>
  );
}

function ConsultationsTab({ headers }) {
  const [rows, setRows] = useState([]);
  const load = async () => setRows((await axios.get(`${BACKEND}/api/admin/consultations`, { headers: headers(), withCredentials: true })).data);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const setStatus = async (id, status) => {
    await axios.patch(`${BACKEND}/api/admin/consultations/${id}`, { status }, { headers: headers(), withCredentials: true });
    toast.success("Updated"); load();
  };
  return (
    <div className="border hairline-navy">
      <div className="grid grid-cols-12 border-b hairline bg-[var(--pta-paper)] font-mono-fine text-[10px] tracking-[0.22em] uppercase text-[var(--pta-ink)]/60">
        <div className="col-span-2 p-3">Slot</div>
        <div className="col-span-3 p-3">Name</div>
        <div className="col-span-3 p-3">Contact</div>
        <div className="col-span-2 p-3">Topic</div>
        <div className="col-span-2 p-3">Status</div>
      </div>
      {rows.length === 0 && <div className="p-8 text-center text-[var(--pta-ink)]/60">No bookings yet.</div>}
      {rows.map((r) => (
        <div key={r.id} className="grid grid-cols-12 border-b hairline last:border-b-0 hover:bg-[var(--pta-paper)]/40">
          <div className="col-span-2 p-3 font-mono-fine text-xs text-[var(--pta-ink)]/70">{r.preferred_date} · {r.preferred_time}</div>
          <div className="col-span-3 p-3 font-serif-display text-base text-[var(--pta-navy)]">{r.name}</div>
          <div className="col-span-3 p-3 text-xs text-[var(--pta-ink)]/70"><div>{r.email}</div><div>{r.phone}</div></div>
          <div className="col-span-2 p-3 text-xs">{r.topic}</div>
          <div className="col-span-2 p-3">
            <Select value={r.status || "pending"} onValueChange={(v) => setStatus(r.id, v)}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="done">Done</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      ))}
    </div>
  );
}

function ListView({ url, headers, columns, title }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { axios.get(url, { headers: headers(), withCredentials: true }).then((r) => setRows(r.data)); }, [url, headers]);
  return (
    <div className="border hairline-navy">
      <div className="p-4 border-b hairline bg-[var(--pta-paper)] font-mono-fine text-[10px] tracking-[0.22em] uppercase text-[var(--pta-ink)]/60">{title} · {rows.length}</div>
      {rows.length === 0 && <div className="p-8 text-center text-[var(--pta-ink)]/60">No records.</div>}
      {rows.map((r, i) => (
        <div key={i} className="grid grid-cols-12 border-b hairline last:border-b-0 p-3 text-sm">
          {columns.map((c, j) => <div key={j} className={c.span || "col-span-3"}>{c.render ? c.render(r) : r[c.key]}</div>)}
        </div>
      ))}
    </div>
  );
}

function UsersTab({ headers }) {
  const [data, setData] = useState({ portal: [], google: [] });
  useEffect(() => { axios.get(`${BACKEND}/api/admin/users`, { headers: headers(), withCredentials: true }).then((r) => setData(r.data)); }, [headers]);
  const rows = useMemo(() => [
    ...(data.google || []).map((u) => ({ email: u.email, name: u.name, source: "Google", when: u.created_at })),
    ...(data.portal || []).map((u) => ({ email: u.email, name: u.name, source: `Password${u.role === "admin" ? " · Admin" : ""}`, when: u.created_at })),
  ], [data]);
  return (
    <div className="border hairline-navy">
      <div className="grid grid-cols-12 border-b hairline bg-[var(--pta-paper)] font-mono-fine text-[10px] tracking-[0.22em] uppercase text-[var(--pta-ink)]/60">
        <div className="col-span-4 p-3">Email</div>
        <div className="col-span-3 p-3">Name</div>
        <div className="col-span-2 p-3">Type</div>
        <div className="col-span-3 p-3">Joined</div>
      </div>
      {rows.map((u, i) => (
        <div key={i} className="grid grid-cols-12 border-b hairline last:border-b-0 p-3 text-sm">
          <div className="col-span-4">{u.email}</div>
          <div className="col-span-3 font-serif-display text-base text-[var(--pta-navy)]">{u.name}</div>
          <div className="col-span-2 font-mono-fine text-[10px] tracking-[0.2em] uppercase text-[var(--pta-gold)]">{u.source}</div>
          <div className="col-span-3 font-mono-fine text-xs text-[var(--pta-ink)]/60">{(u.when || "").slice(0,10)}</div>
        </div>
      ))}
    </div>
  );
}

function ImagePicker({ label, value, onChange, headers }) {
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await axios.post(`${BACKEND}/api/uploads`, fd, { headers: { ...headers(), "Content-Type": "multipart/form-data" }, withCredentials: true });
      onChange(r.data.url);
      toast.success("Image uploaded");
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    setBusy(false);
    e.target.value = "";
  };
  return (
    <div>
      <Label className="overline">{label}</Label>
      <div className="mt-2 flex items-start gap-4">
        {value && <img src={value} alt="preview" className="w-24 h-24 object-cover border hairline-navy" />}
        <div className="flex-1 space-y-2">
          <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Image URL or upload below" />
          <label className="inline-flex items-center gap-2 btn-pta-outline cursor-pointer">
            <UploadSimple size={14} weight="bold" /> {busy ? "…" : "Upload image"}
            <input type="file" accept="image/*" onChange={onFile} className="hidden" />
          </label>
        </div>
      </div>
    </div>
  );
}

function CMSTab({ headers, cms, reload }) {
  const [hero, setHero] = useState(cms.hero || {});
  const [about, setAbout] = useState(cms.about || {});
  const [testimonials, setTestimonials] = useState(cms.testimonials || []);
  const [caseStudies, setCaseStudies] = useState(cms.case_studies || []);
  const [faqs, setFaqs] = useState(cms.faqs || []);
  const [industries, setIndustries] = useState(cms.industries || []);
  const [saving, setSaving] = useState("");

  const save = async (section, data) => {
    setSaving(section);
    try {
      await axios.put(`${BACKEND}/api/content`, { section, data }, { headers: headers(), withCredentials: true });
      toast.success(`${section} saved`);
      invalidateSiteContent();
      reload();
    } catch (e) { toast.error("Save failed"); }
    setSaving("");
  };

  return (
    <Tabs defaultValue="hero">
      <TabsList className="flex flex-wrap h-auto p-1 bg-[var(--pta-paper)] border hairline-navy">
        {[["hero","Hero"],["about","About"],["testimonials","Testimonials"],["case","Case Studies"],["faqs","FAQs"],["industries","Industries"]].map(([v,l])=>(
          <TabsTrigger key={v} value={v} className="font-mono-fine text-[11px] tracking-[0.2em] uppercase data-[state=active]:bg-[var(--pta-navy)] data-[state=active]:text-[var(--pta-gold)]">{l}</TabsTrigger>
        ))}
      </TabsList>

      {/* HERO */}
      <TabsContent value="hero" className="mt-6 space-y-5 border hairline-navy p-6">
        <div><Label className="overline">Chapter overline</Label><Input value={hero.chapter || ""} onChange={(e)=>setHero({...hero,chapter:e.target.value})} className="mt-2" /></div>
        <div><Label className="overline">Hero lines (comma separated, last one is highlighted)</Label>
          <Input value={(hero.lines || []).join(",")} onChange={(e)=>setHero({...hero,lines:e.target.value.split(",").map(s=>s.trim())})} className="mt-2" />
        </div>
        <div><Label className="overline">Subheading</Label><Textarea rows={3} value={hero.subheading || ""} onChange={(e)=>setHero({...hero,subheading:e.target.value})} className="mt-2" /></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label className="overline">CTA primary</Label><Input value={hero.cta_primary || ""} onChange={(e)=>setHero({...hero,cta_primary:e.target.value})} className="mt-2" /></div>
          <div><Label className="overline">CTA secondary</Label><Input value={hero.cta_secondary || ""} onChange={(e)=>setHero({...hero,cta_secondary:e.target.value})} className="mt-2" /></div>
        </div>
        <div><Label className="overline">Founder badge</Label><Input value={hero.founder_badge || ""} onChange={(e)=>setHero({...hero,founder_badge:e.target.value})} className="mt-2" /></div>
        <ImagePicker label="Hero image" value={hero.image_url} onChange={(v)=>setHero({...hero,image_url:v})} headers={headers} />
        <button data-testid="save-hero" onClick={()=>save("hero",hero)} className="btn-pta">{saving==="hero"?"Saving…":"Save Hero"} <FloppyDisk size={14} weight="bold" /></button>
      </TabsContent>

      {/* ABOUT */}
      <TabsContent value="about" className="mt-6 space-y-5 border hairline-navy p-6">
        <ImagePicker label="Portrait" value={about.portrait_url} onChange={(v)=>setAbout({...about,portrait_url:v})} headers={headers} />
        <ImagePicker label="Home advisory image (Chapter 04)" value={about.advisory_image_url} onChange={(v)=>setAbout({...about,advisory_image_url:v})} headers={headers} />
        <div>
          <Label className="overline">Paragraphs</Label>
          <div className="mt-2 space-y-3">
            {(about.paragraphs || []).map((p, i) => (
              <div key={i} className="flex gap-2">
                <Textarea rows={3} value={p} onChange={(e) => { const c = [...(about.paragraphs || [])]; c[i] = e.target.value; setAbout({...about, paragraphs: c}); }} />
                <button className="btn-pta-outline" onClick={() => setAbout({...about, paragraphs: about.paragraphs.filter((_, j) => j !== i)})}><Trash size={14} /></button>
              </div>
            ))}
            <button className="btn-pta-outline" onClick={()=>setAbout({...about, paragraphs: [...(about.paragraphs||[]), ""]})}><Plus size={14} /> Add paragraph</button>
          </div>
        </div>
        <div><Label className="overline">Pull-quote</Label><Textarea rows={2} value={about.quote || ""} onChange={(e)=>setAbout({...about,quote:e.target.value})} className="mt-2" /></div>
        <button data-testid="save-about" onClick={()=>save("about",about)} className="btn-pta">{saving==="about"?"Saving…":"Save About"} <FloppyDisk size={14} weight="bold" /></button>
      </TabsContent>

      {/* TESTIMONIALS */}
      <TabsContent value="testimonials" className="mt-6 space-y-4 border hairline-navy p-6">
        {testimonials.map((t, i) => (
          <div key={i} className="border hairline p-4 space-y-2">
            <Textarea rows={2} value={t.quote} onChange={(e)=>{ const c=[...testimonials]; c[i]={...c[i],quote:e.target.value}; setTestimonials(c);}} placeholder="Quote" />
            <div className="grid grid-cols-2 gap-2">
              <Input value={t.author} onChange={(e)=>{ const c=[...testimonials]; c[i]={...c[i],author:e.target.value}; setTestimonials(c);}} placeholder="Author" />
              <Input value={t.role} onChange={(e)=>{ const c=[...testimonials]; c[i]={...c[i],role:e.target.value}; setTestimonials(c);}} placeholder="Role" />
            </div>
            <button className="btn-pta-outline" onClick={()=>setTestimonials(testimonials.filter((_,j)=>j!==i))}><Trash size={14}/> Remove</button>
          </div>
        ))}
        <button className="btn-pta-outline" onClick={()=>setTestimonials([...testimonials,{quote:"",author:"",role:""}])}><Plus size={14}/> Add testimonial</button>
        <button data-testid="save-testimonials" onClick={()=>save("testimonials",testimonials)} className="btn-pta ml-2">{saving==="testimonials"?"Saving…":"Save Testimonials"}</button>
      </TabsContent>

      {/* CASE STUDIES */}
      <TabsContent value="case" className="mt-6 space-y-4 border hairline-navy p-6">
        {caseStudies.map((c, i) => (
          <div key={i} className="border hairline p-4 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <Input value={c.number} onChange={(e)=>{ const cc=[...caseStudies]; cc[i]={...cc[i],number:e.target.value}; setCaseStudies(cc);}} placeholder="Headline number (₹25 L)" />
              <Input value={c.title} onChange={(e)=>{ const cc=[...caseStudies]; cc[i]={...cc[i],title:e.target.value}; setCaseStudies(cc);}} placeholder="Title" />
            </div>
            <Textarea rows={2} value={c.detail} onChange={(e)=>{ const cc=[...caseStudies]; cc[i]={...cc[i],detail:e.target.value}; setCaseStudies(cc);}} placeholder="Detail" />
            <button className="btn-pta-outline" onClick={()=>setCaseStudies(caseStudies.filter((_,j)=>j!==i))}><Trash size={14}/> Remove</button>
          </div>
        ))}
        <button className="btn-pta-outline" onClick={()=>setCaseStudies([...caseStudies,{title:"",number:"",detail:""}])}><Plus size={14}/> Add case</button>
        <button data-testid="save-cases" onClick={()=>save("case_studies",caseStudies)} className="btn-pta ml-2">{saving==="case_studies"?"Saving…":"Save Case Studies"}</button>
      </TabsContent>

      {/* FAQS */}
      <TabsContent value="faqs" className="mt-6 space-y-4 border hairline-navy p-6">
        {faqs.map((f, i) => (
          <div key={i} className="border hairline p-4 space-y-2">
            <Input value={f.q} onChange={(e)=>{ const c=[...faqs]; c[i]={...c[i],q:e.target.value}; setFaqs(c);}} placeholder="Question" />
            <Textarea rows={2} value={f.a} onChange={(e)=>{ const c=[...faqs]; c[i]={...c[i],a:e.target.value}; setFaqs(c);}} placeholder="Answer" />
            <button className="btn-pta-outline" onClick={()=>setFaqs(faqs.filter((_,j)=>j!==i))}><Trash size={14}/> Remove</button>
          </div>
        ))}
        <button className="btn-pta-outline" onClick={()=>setFaqs([...faqs,{q:"",a:""}])}><Plus size={14}/> Add FAQ</button>
        <button data-testid="save-faqs" onClick={()=>save("faqs",faqs)} className="btn-pta ml-2">{saving==="faqs"?"Saving…":"Save FAQs"}</button>
      </TabsContent>

      {/* INDUSTRIES */}
      <TabsContent value="industries" className="mt-6 space-y-4 border hairline-navy p-6">
        {industries.map((it, i) => (
          <div key={i} className="border hairline p-4 grid grid-cols-1 md:grid-cols-2 gap-2">
            <Input value={it.name} onChange={(e)=>{ const c=[...industries]; c[i]={...c[i],name:e.target.value}; setIndustries(c);}} placeholder="Industry" />
            <div className="flex gap-2">
              <Input value={it.note} onChange={(e)=>{ const c=[...industries]; c[i]={...c[i],note:e.target.value}; setIndustries(c);}} placeholder="Note" />
              <button className="btn-pta-outline" onClick={()=>setIndustries(industries.filter((_,j)=>j!==i))}><Trash size={14}/></button>
            </div>
          </div>
        ))}
        <button className="btn-pta-outline" onClick={()=>setIndustries([...industries,{name:"",note:""}])}><Plus size={14}/> Add industry</button>
        <button data-testid="save-industries" onClick={()=>save("industries",industries)} className="btn-pta ml-2">{saving==="industries"?"Saving…":"Save Industries"}</button>
      </TabsContent>
    </Tabs>
  );
}

export default function Admin() {
  const { user, token, setToken, headers, checking } = useAdminAuth();
  const nav = useNavigate();
  const [cms, setCms] = useState({});
  const reloadCms = async () => setCms((await axios.get(`${BACKEND}/api/content`)).data);
  useEffect(() => { if (user?.role === "admin") reloadCms(); }, [user]);

  const logout = async () => {
    try { await axios.post(`${BACKEND}/api/auth/logout`, {}, { withCredentials: true }); } catch (_) {}
    localStorage.removeItem("pta_portal_token"); setToken(null);
    nav("/portal");
  };

  if (checking) {
    return <section className="pt-24 pb-24 text-center bg-[var(--pta-cream)] min-h-[70vh]"><div className="chapter-num">Admin</div><div className="mt-4 font-serif-display text-3xl text-[var(--pta-navy)]">Checking…</div></section>;
  }
  if (!user || user.role !== "admin") {
    return (
      <section className="pt-24 pb-24 bg-[var(--pta-cream)] min-h-[70vh]">
        <div className="max-w-[600px] mx-auto px-6 text-center">
          <div className="chapter-num">Admin</div>
          <h1 className="font-serif-display text-5xl text-[var(--pta-navy)] mt-4">Restricted.</h1>
          <p className="mt-4 text-[var(--pta-ink)]/70">Sign in with the admin account to access the CMS.</p>
          <button onClick={() => nav("/portal")} className="btn-pta mt-6">Go to sign-in</button>
        </div>
      </section>
    );
  }

  return (
    <section className="pt-16 md:pt-24 pb-24 bg-[var(--pta-cream)] min-h-[70vh]">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
          <div>
            <div className="chapter-num">Admin · Control Room</div>
            <h1 className="font-serif-display text-5xl md:text-6xl text-[var(--pta-navy)] mt-3">Hi, Pankaj.</h1>
            <div className="mt-2 font-mono-fine text-[10px] tracking-[0.22em] uppercase text-[var(--pta-gold)]">{user.email} · Role · Admin</div>
          </div>
          <div className="flex gap-3">
            <button onClick={reloadCms} className="btn-pta-outline"><ArrowsClockwise size={14} weight="bold" /> Refresh</button>
            <button data-testid="admin-logout" onClick={logout} className="btn-pta"><SignOut size={14} weight="bold" /> Sign out</button>
          </div>
        </div>

        <Stats headers={headers} />

        <div className="mt-10">
          <Tabs defaultValue="enquiries">
            <TabsList className="flex flex-wrap h-auto p-1 bg-[var(--pta-paper)] border hairline-navy">
              {[["enquiries","Enquiries"],["consultations","Consultations"],["newsletter","Newsletter"],["users","Users"],["cms","CMS"]].map(([v,l])=>(
                <TabsTrigger key={v} value={v} data-testid={`admin-tab-${v}`} className="font-mono-fine text-[11px] tracking-[0.2em] uppercase data-[state=active]:bg-[var(--pta-navy)] data-[state=active]:text-[var(--pta-gold)]">{l}</TabsTrigger>
              ))}
            </TabsList>
            <div className="mt-6">
              <TabsContent value="enquiries"><EnquiriesTab headers={headers} /></TabsContent>
              <TabsContent value="consultations"><ConsultationsTab headers={headers} /></TabsContent>
              <TabsContent value="newsletter">
                <ListView url={`${BACKEND}/api/admin/newsletter`} headers={headers} title="Subscribers" columns={[
                  {key:"email", span:"col-span-8"},
                  {span:"col-span-4 font-mono-fine text-xs text-[var(--pta-ink)]/60", render: (r)=> (r.created_at||"").slice(0,10)},
                ]} />
              </TabsContent>
              <TabsContent value="users"><UsersTab headers={headers} /></TabsContent>
              <TabsContent value="cms">{cms.hero ? <CMSTab headers={headers} cms={cms} reload={reloadCms} /> : <div className="text-[var(--pta-ink)]/60">Loading CMS…</div>}</TabsContent>
            </div>
          </Tabs>
        </div>
      </div>
    </section>
  );
}
